/* Living Fuel #9. Existing Shopify native bundles + current Moon offer; no new discounts. */
(() => {
  'use strict';
  const rules = typeof module !== 'undefined' && module.exports
    ? require('./lf-cart-offer-rules.js') : window.LFCartOfferRules;
  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  const same = (a, b) => rules.fingerprint(a) === rules.fingerprint(b);
  const clone = value => JSON.parse(JSON.stringify(value));
  const synthetic = addition => ({ id: addition.id, quantity: addition.quantity, properties: addition.properties || {} });
  const expected = (cart, addition, source) => ({ items: [
    ...cart.items.filter(item => !source || rules.identity(item) !== rules.identity(source)),
    ...(addition ? [synthetic(addition)] : [])
  ] });
  const reviewMessage = 'Your cart changed while the offer was being applied. Please review the items and prices before checkout.';
  function pricesMatch(cart, before, addition, source, expectedLinePrice) {
    if (before.currency && cart.currency !== before.currency) return false;
    const remaining = before.items.filter(item => !source || rules.identity(item) !== rules.identity(source));
    if (!remaining.every(item => cart.items.some(current => rules.identity(current) === rules.identity(item) &&
        current.final_line_price === item.final_line_price))) return false;
    const added = cart.items.find(item => rules.identity(item) === rules.identity(synthetic(addition)));
    if (!added || added.final_line_price !== expectedLinePrice) return false;
    return !Number.isFinite(before.total_price) || cart.total_price === before.total_price + expectedLinePrice - (source?.final_line_price || 0);
  }

  const nonnegativeCents = value => Number.isSafeInteger(value) && value >= 0;
  function discountCodes(cart) {
    const codes = new Set();
    for (const code of cart.discount_codes || []) {
      if (code && code.code && code.applicable !== false) codes.add(String(code.code).toLowerCase());
    }
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || []),
      ...cart.items.flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application))
    ];
    for (const app of applications) {
      if (app?.type === 'discount_code' && app.title) codes.add(String(app.title).toLowerCase());
    }
    return codes;
  }
  function addPricesSafe(cart, before, addition, maximumLinePrice) {
    if (cart.currency !== 'USD' || before.currency !== 'USD' || !nonnegativeCents(maximumLinePrice) ||
        !nonnegativeCents(cart.total_price) || !nonnegativeCents(before.total_price)) return false;
    const added = cart.items.find(item => rules.identity(item) === rules.identity(synthetic(addition)));
    if (!added || !nonnegativeCents(added.final_line_price) || added.final_line_price > maximumLinePrice ||
        cart.total_price > before.total_price + maximumLinePrice) return false;
    // An existing code can redistribute its allocation or unlock a threshold
    // discount. Preserve base prices/identities, not the old discount allocation.
    if (!before.items.every(item => {
      const current = cart.items.find(candidate => rules.identity(candidate) === rules.identity(item));
      return current && ['original_price', 'original_line_price'].every(field =>
        item[field] == null || current[field] === item[field]);
    })) return false;
    if (added.original_line_price != null && added.original_line_price !== maximumLinePrice) return false;
    const currentCodes = discountCodes(cart);
    return [...discountCodes(before)].every(code => currentCodes.has(code));
  }

  // Every mutation is followed by a fresh read, including uncertain network outcomes.
  // No blind retries, variant-ID removals, cart clears, or wholesale cart restores.
  async function mutateAndRead(api, path, body) {
    try { await api(path, body); } catch (_) { /* The server may have completed it. */ }
    return api('cart.js');
  }
  async function removeExact(api, cart, line) {
    const current = cart.items.find(item => rules.identity(item) === rules.identity(line));
    if (!current || !current.key) throw new Error(reviewMessage);
    return mutateAndRead(api, 'cart/update.js', { updates: { [current.key]: 0 } });
  }
  async function replaceSafely(api, before, addition, source, expectedLinePrice, discountAwareAdd = false) {
    if (discountAwareAdd && source) throw new Error(reviewMessage);
    const withAdded = expected(before, addition);
    let cart = await mutateAndRead(api, 'cart/add.js', { items: [addition] });
    if (same(cart, before)) throw new Error('This offer could not be added. Your original items were kept; please review prices and discounts before checkout.');
    if (!same(cart, withAdded)) throw new Error(reviewMessage);
    const added = cart.items.find(item => rules.identity(item) === rules.identity(synthetic(addition)));
    if (!added || !(discountAwareAdd
      ? addPricesSafe(cart, before, addition, expectedLinePrice)
      : pricesMatch(cart, before, addition, null, expectedLinePrice))) {
      cart = await removeExact(api, cart, added || synthetic(addition));
      if (same(cart, before)) throw new Error('The offer price changed, so it was not applied. Your original items were kept; please review prices and discounts before checkout.');
      throw new Error(reviewMessage);
    }
    if (!source) return cart;
    // Reread keys and validate the entire semantic cart before removing any original item.
    cart = await api('cart.js');
    if (!same(cart, withAdded)) throw new Error(reviewMessage);
    if (!pricesMatch(cart, before, addition, null, expectedLinePrice)) {
      await removeExact(api, cart, added);
      throw new Error('The offer price changed. Your original items were kept; please review the cart.');
    }
    cart = await removeExact(api, cart, source);
    if (same(cart, expected(before, addition, source))) {
      if (!pricesMatch(cart, before, addition, source, expectedLinePrice)) {
        throw new Error('The cart was updated, but the offer price changed. Please review the items and prices before checkout.');
      }
      return cart;
    }
    // Only roll back the exact addition when every original item is still intact.
    if (same(cart, withAdded)) {
      cart = await removeExact(api, cart, added);
      if (same(cart, before)) throw new Error('The upgrade could not finish. Your original items are still in the cart.');
    }
    throw new Error(reviewMessage);
  }

  function currentMoonOffer(config, payload) {
    try {
      const target = config.products.sg3;
      const bundles = payload.bundles.filter(b => b.settings?.products?.some(p => String(p.id).split('/').pop() === String(target.id)));
      if (bundles.length !== 1) return null;
      const bundle = bundles[0], settings = bundle.settings;
      if (settings.products.length !== 1 || bundle.offers.length !== 1 || Number(settings.defaultIdx) !== 0 ||
          settings.market !== 'all' || settings.showTimer || settings.priceRounding || bundle.subscription ||
          bundle.gifts?.length || bundle.upsells?.length) return null;
      const product = settings.products[0], offer = bundle.offers[0], variant = product.variants?.[0];
      if (product.variants.length !== 1 || Number(product.quantity) !== 1 || product.discount !== 'none' ||
          String(variant.id).split('/').pop() !== String(target.variantId) || !variant.available ||
          Number(variant.price) !== target.price || Number(offer.qty) !== 1 || offer.soldOut !== false ||
          offer.discount !== 'percent' || !Number.isFinite(Number(offer.discountValue)) ||
          Number(offer.discountValue) <= 0 || Number(offer.discountValue) >= 100 || !bundle.id || !offer.id) return null;
      return {
        price: Math.round(target.price * (100 - Number(offer.discountValue)) / 100),
        properties: { __moonbundle: JSON.stringify({ offer_id: offer.id, bundle_id: bundle.id,
          main_product_id: String(target.id), type: 'bundle' }) }
      };
    } catch (_) { return null; }
  }

  function pageSectionPlan(doc) {
    const items = doc.querySelector('#main-cart-items');
    const footer = doc.querySelector('#main-cart-footer');
    if (!items?.dataset.id || !footer?.dataset.id) throw new Error(reviewMessage);
    const plan = [
      { section: items.dataset.id, target: '#main-cart-items .js-contents', source: '#main-cart-items .js-contents' },
      { section: footer.dataset.id, target: '#main-cart-footer .js-contents', source: '#main-cart-footer .js-contents' },
      { section: footer.dataset.id, target: '#LF-CartOffer-Page', source: '.lf-cart-offer-slot' },
      { section: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' },
      { section: 'cart-live-region-text', target: '#cart-live-region-text', source: '.shopify-section' }
    ];
    if (doc.querySelector('cart-drawer')) plan.push({
      section: 'cart-drawer', target: 'cart-drawer .drawer__inner', source: 'cart-drawer .drawer__inner', copyClass: true
    });
    return plan;
  }

  // Parse and validate every source before changing the DOM. Keep outer containers,
  // cart notes, native listeners and the drawer overlay listener intact.
  function renderPageSections(doc, sections, cart, parseHTML) {
    const parsed = new Map();
    const updates = pageSectionPlan(doc).map(part => {
      if (typeof sections[part.section] !== 'string') throw new Error(reviewMessage);
      if (!parsed.has(part.section)) parsed.set(part.section, parseHTML(sections[part.section]));
      const source = parsed.get(part.section).querySelector(part.source);
      const target = doc.querySelector(part.target);
      if (!source || !target) throw new Error(reviewMessage);
      return { target, html: source.innerHTML, className: part.copyClass ? source.className : null };
    });
    updates.forEach(update => {
      update.target.innerHTML = update.html;
      if (update.className !== null) update.target.className = update.className;
    });
    ['cart-items', '#main-cart-footer', 'cart-drawer'].forEach(selector => {
      doc.querySelector(selector)?.classList.toggle('is-empty', cart.item_count === 0);
    });
    const checkout = doc.querySelector('#checkout');
    if (checkout) checkout.disabled = cart.item_count === 0;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { replaceSafely, removeExact, currentMoonOffer, same, pageSectionPlan, renderPageSections, addPricesSafe, discountCodes };
    return;
  }
  if (!rules || customElements.get('lf-cart-offer')) return;
  const state = { busy: false, undo: null, message: '' };
  const root = window.Shopify?.routes?.root || '/';
  async function api(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET', cache: 'no-store',
      // /cart with Accept: application/json returns cart data, not requested HTML sections.
      headers: { 'Accept': path.includes('sections=') ? '*/*' : 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error('Cart request could not be completed.');
    return result;
  }
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  function lock(value) {
    state.busy = value;
    const drawer = document.querySelector('cart-drawer');
    drawer?.setAttribute('aria-busy', String(value));
    ['#CartDrawer', '#main-cart-items', '#main-cart-footer', '#cart-icon-bubble'].forEach(selector => {
      const target = document.querySelector(selector);
      target?.toggleAttribute('inert', value);
      target?.setAttribute('aria-busy', String(value));
    });
  }
  async function renderCart(cart) {
    if (document.querySelector('#LF-CartOffer-Page')) {
      const ids = [...new Set(pageSectionPlan(document).map(part => part.section))];
      const sections = await api('cart?sections=' + encodeURIComponent(ids.join(',')));
      renderPageSections(document, sections, cart, html => new DOMParser().parseFromString(html, 'text/html'));
      await document.querySelector('lf-cart-offer')?.refresh(cart);
      // All page totals/items and the hidden drawer were updated without opening it.
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'lf-cart-offer', cartData: cart, sectionsRendered: true
      });
      return;
    }
    // Preserve the product/page context; /cart suppresses the hidden drawer offer.
    const path = window.location.pathname.startsWith(root)
      ? window.location.pathname.slice(root.length) : window.location.pathname.replace(/^\/+/, '');
    const sections = await api(path + '?sections=cart-drawer,cart-icon-bubble');
    if (!sections['cart-drawer'] || !sections['cart-icon-bubble']) throw new Error(reviewMessage);
    const drawer = document.querySelector('cart-drawer');
    drawer?.renderContents({ ...cart, sections });
    drawer?.classList.toggle('is-empty', cart.item_count === 0);
    await document.querySelector('lf-cart-offer')?.refresh(cart);
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lf-cart-offer', cartData: cart, sectionsRendered: true });
  }

  class LFCartOffer extends HTMLElement {
    connectedCallback() {
      try { this.config = JSON.parse(this.querySelector('[data-lf-offer-config]').textContent); }
      catch (_) { return; }
      this.content = this.querySelector('.lf-cart-offer__content');
      this.refresh();
    }
    moon() {
      const payload = document.querySelector('#moonbundle-bundle-config');
      if (!payload) return null;
      try { return currentMoonOffer(this.config, JSON.parse(payload.textContent)); }
      catch (_) { return null; }
    }
    async refresh(suppliedCart) {
      const revision = (this.refreshRevision || 0) + 1;
      this.refreshRevision = revision;
      try {
        const cart = suppliedCart || await api('cart.js');
        if (!this.isConnected || this.refreshRevision !== revision) return;
        if (state.undo && !same(cart, state.undo.after)) state.undo = null;
        if (state.message) return this.showStatus(state.message, !!state.undo);
        this.offer = rules.select(cart, this.config, this.moon());
        this.content.replaceChildren();
        this.hidden = !this.offer;
        if (this.offer) this.paint(this.offer);
      } catch (_) { if (this.refreshRevision === revision) this.hidden = true; }
    }
    paint(offer) {
      const upgrade = offer.kind === 'upgrade', greens = offer.id === 'sg-three';
      this.content.append(node('h2', 'lf-cart-offer__heading', upgrade ? 'Stock up with a 3-pack' : 'Another way to fuel your day'));
      const row = node('div', 'lf-cart-offer__row');
      const img = node('img', 'lf-cart-offer__image');
      img.src = offer.target.image; img.alt = ''; img.width = 64; img.height = 76; img.loading = 'lazy';
      const details = node('div', '');
      const link = node('a', 'lf-cart-offer__title', offer.target.title); link.href = offer.target.url;
      details.append(link);
      const description = upgrade
        ? `Replace your 2 ${greens ? 'SuperGreens cans' : 'Omegas bottles'} with a 3-pack. One extra ${greens ? 'can' : 'bottle'} for ${money(offer.increment)} more.`
        : offer.id === 'add-protein'
          ? 'Want a protein shake for another part of your day? Enjoy mild vanilla LivingProtein with water, your favorite milk, or a smoothie recipe.'
          : offer.id === 'add-omegas'
            ? 'Already choosing SuperGreens and LivingProtein? Add Omegas as an optional omega-3 supplement for your routine.'
            : 'Looking for a drinkable Super Meal, too? SuperGreens combines plant protein, fiber and a broad blend of nutrients, with a mild greens taste.';
      details.append(node('p', 'lf-cart-offer__copy', description));
      details.append(node('p', 'lf-cart-offer__price', upgrade ? `${money(offer.price)} for the 3-pack` : `${money(offer.price)} before eligible discounts · one-time addition`));
      if (upgrade) {
        const single = this.config.products[greens ? 'sg' : 'omegas'];
        details.append(node('p', 'lf-cart-offer__copy', `Save ${money(3 * single.price - offer.price)} vs. 3 single ${greens ? 'cans' : 'bottles'} at full retail.`));
      }
      row.append(img, details); this.content.append(row);
      this.content.append(node('p', 'lf-cart-offer__terms', upgrade
        ? 'One-time purchase. Replaces only these two items; other items and subscriptions stay unchanged.'
        : 'Optional. No subscription added or changed. Eligible discounts are calculated in your updated cart; shipping and taxes at checkout.'));
      const button = node('button', 'button lf-cart-offer__button', upgrade
        ? `Upgrade to 3 · +${money(offer.increment)}` : `Add ${offer.id === 'add-protein' ? 'LivingProtein' : offer.id === 'add-omegas' ? 'Omegas' : 'SuperGreens'}`);
      button.type = 'button'; button.addEventListener('click', () => this.apply()); this.content.append(button);
    }
    showStatus(message, undo) {
      this.hidden = false; this.content.replaceChildren();
      const status = node('p', 'lf-cart-offer__status', message);
      status.setAttribute('role', 'status'); status.tabIndex = -1;
      this.content.append(status);
      if (undo) {
        const button = node('button', 'lf-cart-offer__undo', 'Undo'); button.type = 'button';
        button.addEventListener('click', () => this.undo()); this.content.append(button);
      }
    }
    async apply() {
      if (state.busy || !this.offer) return;
      if (document.querySelector('.cart__items--disabled')) {
        this.showStatus('Please wait for the current cart update, then try the offer again.', false);
        return;
      }
      const shown = this.offer;
      lock(true); this.showStatus('Updating your cart…', false);
      try {
        const before = await api('cart.js');
        const offer = rules.select(before, this.config, this.moon());
        if (!offer || offer.id !== shown.id || offer.price !== shown.price || offer.increment !== shown.increment) {
          throw new Error('Your cart or offer changed. Please refresh the cart to see the current recommendation.');
        }
        const product = await api(offer.target.url.replace(/^\//, '') + '.js');
        const variant = product.variants.find(v => Number(v.id) === offer.target.variantId);
        if (!variant?.available || Number(variant.price) !== offer.target.price) {
          throw new Error('This product’s price or availability changed. Please refresh before adding it.');
        }
        const addition = { id: offer.target.variantId, quantity: 1, properties: offer.properties || {} };
        const after = await replaceSafely(api, before, addition, offer.source, offer.price, offer.kind === 'add');
        state.undo = { before: clone(before), after: clone(after), offer: clone(offer), addition };
        state.message = offer.kind === 'upgrade'
          ? `Upgraded to ${offer.target.title}. Your other items and subscriptions are unchanged.`
          : `Added ${offer.target.title} as a one-time purchase.`;
        try { await renderCart(after); }
        catch (_) { this.showStatus(state.message + ' Refresh the page to view the updated cart.', true); }
      } catch (error) {
        state.undo = null;
        state.message = error.message || reviewMessage;
        try { await renderCart(await api('cart.js')); } catch (_) { this.showStatus(state.message + ' Refresh the page to confirm your cart.', false); }
      } finally {
        lock(false);
        document.querySelector('lf-cart-offer .lf-cart-offer__status')?.focus();
      }
    }
    async undo() {
      if (state.busy || !state.undo) return;
      if (document.querySelector('.cart__items--disabled')) return;
      const saved = state.undo;
      lock(true); this.showStatus('Undoing the offer…', false);
      try {
        const cart = await api('cart.js');
        if (!same(cart, saved.after)) throw new Error(reviewMessage);
        const added = cart.items.find(item => rules.identity(item) === rules.identity(synthetic(saved.addition)));
        let restored;
        if (saved.offer.source) {
          const original = saved.offer.source;
          restored = await replaceSafely(api, cart, { id: original.id, quantity: original.quantity, properties: original.properties || {} }, added, original.final_line_price);
        } else restored = await removeExact(api, cart, added);
        if (!same(restored, saved.before)) throw new Error(reviewMessage);
        state.undo = null; state.message = 'Offer undone. Your original items are back in the cart.';
        await renderCart(restored);
      } catch (error) {
        state.undo = null; state.message = error.message || reviewMessage;
        try { await renderCart(await api('cart.js')); } catch (_) { this.showStatus(state.message, false); }
      } finally {
        lock(false);
        document.querySelector('lf-cart-offer .lf-cart-offer__status')?.focus();
      }
    }
  }
  customElements.define('lf-cart-offer', LFCartOffer);
  // A later ordinary cart edit clears transient confirmation; never persist cart state in storage.
  if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
    if (event.source === 'lf-cart-offer' || state.busy) return;
    state.message = ''; state.undo = null;
    document.querySelector('lf-cart-offer')?.refresh();
  });
})();
